"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { Loader2, Upload, Paperclip, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { submitMultipartData } from '@/utils/api/getData';
import { apiRoutes } from '@/utils/api/routes';
import { setProfessionalAccessCookie } from '@/actions/set-professional-cookie';
import { IPROFESSIONAL_SERVICE } from '@/utils/interface';

export interface RequestServiceButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    service?: IPROFESSIONAL_SERVICE;
    professionalId?: string;
    label?: React.ReactNode;
    className?: string;
    user?: { user: string; token: string; type: string; url?: string } | null;
}

export interface ServiceRequestFormData {
    name: string;
    email: string;
    subject: string;
    message: string;
    file?: FileList;
}

export function RequestServiceButton({
    service,
    professionalId,
    label = "Request This Service",
    className = "",
    user,
    onClick,
    ...props
}: RequestServiceButtonProps) {
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors }
    } = useForm<ServiceRequestFormData>({
        defaultValues: {
            name: '',
            email: '',
            subject: service?.title || '',
            message: '',
        }
    });

    const targetProfessionalId = professionalId || (service?.professional as any)?._id || service?.professional;

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick) {
            onClick(e);
        }

        if (!user?.token || !user?.user) {
            if (targetProfessionalId) {
                await setProfessionalAccessCookie(String(targetProfessionalId));
            }
            setIsAuthModalOpen(true);
            return;
        }

        if (service?.title) {
            setValue('subject', service.title);
        }

        setIsFormModalOpen(true);
    };

    const handleFormSubmit = async (data: ServiceRequestFormData) => {
        if (!user?.token) {
            setIsAuthModalOpen(true);
            return;
        }

        try {
            setLoading(true);

            const formData = new FormData();
            formData.append('name', data.name);
            formData.append('email', data.email);
            formData.append('subject', data.subject);
            formData.append('message', data.message);
            if (targetProfessionalId) {
                formData.append('professional', String(targetProfessionalId));
            }

            if (data.file && data.file.length > 0) {
                formData.append('file', data.file[0]);
            }

            const response = await submitMultipartData({
                url: apiRoutes.clientRoutes.serviceRequest,
                token: user.token,
                options: {
                    method: 'POST',
                    body: formData,
                },
            });

            if (response?.status || response?.success) {
                toast.success('Service request submitted successfully!');
                reset();
                setSelectedFileName(null);
                setIsFormModalOpen(false);
            } else {
                toast.error(response?.message || 'Failed to submit service request. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting service request:', error);
            toast.error('An error occurred while submitting your request.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={handleClick}
                className={`w-full text-xs font-semibold py-2 px-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all text-center cursor-pointer ${className}`}
                {...props}
            >
                {label}
            </button>

            {/* Service Request Form Modal */}
            <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
                <DialogContent className="sm:max-w-lg p-6 rounded-2xl border border-slate-100 shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-slate-900 tracking-tight">
                            Request Service
                        </DialogTitle>
                        <DialogDescription className="text-xs text-slate-500 mt-1 leading-relaxed">
                            {service?.title
                                ? `Submit your project requirements for "${service.title}".`
                                : "Fill out the form below to request service details."}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 mt-2">
                        {/* Name */}
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1">
                                Full Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Enter your full name"
                                {...register('name', { required: 'Name is required' })}
                                className="w-full text-sm p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800 placeholder:text-slate-400"
                            />
                            {errors.name && (
                                <span className="text-xs text-red-500 mt-1 block">{errors.name.message}</span>
                            )}
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1">
                                Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                placeholder="Enter your email address"
                                {...register('email', {
                                    required: 'Email is required',
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: 'Invalid email address'
                                    }
                                })}
                                className="w-full text-sm p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800 placeholder:text-slate-400"
                            />
                            {errors.email && (
                                <span className="text-xs text-red-500 mt-1 block">{errors.email.message}</span>
                            )}
                        </div>

                        {/* Subject */}
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1">
                                Subject <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="e.g. Architectural Design Consultation"
                                {...register('subject', { required: 'Subject is required' })}
                                className="w-full text-sm p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800 placeholder:text-slate-400"
                            />
                            {errors.subject && (
                                <span className="text-xs text-red-500 mt-1 block">{errors.subject.message}</span>
                            )}
                        </div>

                        {/* Message */}
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1">
                                Message / Details <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                rows={4}
                                placeholder="Describe your project requirements, scope, timeline..."
                                {...register('message', { required: 'Message is required' })}
                                className="w-full text-sm p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none transition-all text-slate-800 placeholder:text-slate-400"
                            />
                            {errors.message && (
                                <span className="text-xs text-red-500 mt-1 block">{errors.message.message}</span>
                            )}
                        </div>

                        {/* Attachment (File) */}
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1">
                                Attachment (Optional)
                            </label>
                            <div className="relative border border-dashed border-slate-300 rounded-xl p-3 bg-slate-50/50 hover:bg-slate-50 transition-colors flex items-center justify-between">
                                <div className="flex items-center gap-2 text-xs text-slate-600 truncate">
                                    <Paperclip className="w-4 h-4 text-blue-600 shrink-0" />
                                    <span className="truncate">
                                        {selectedFileName || "Upload project brief or blueprint (PDF, Image, Doc)"}
                                    </span>
                                </div>
                                <input
                                    type="file"
                                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                    {...register('file', {
                                        onChange: (e) => {
                                            const file = e.target.files?.[0];
                                            setSelectedFileName(file ? file.name : null);
                                        }
                                    })}
                                />
                                {selectedFileName ? (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setValue('file', undefined);
                                            setSelectedFileName(null);
                                        }}
                                        className="text-slate-400 hover:text-slate-600 z-10"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                ) : (
                                    <Upload className="w-4 h-4 text-slate-400 shrink-0" />
                                )}
                            </div>
                        </div>

                        <DialogFooter className="pt-4 flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3 w-full">
                            <button
                                type="button"
                                disabled={loading}
                                onClick={() => setIsFormModalOpen(false)}
                                className="w-full sm:w-1/2 py-2.5 px-4 text-sm font-semibold text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200/80 rounded-xl transition-all text-center cursor-pointer disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full sm:w-1/2 py-2.5 px-4 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl shadow-md hover:shadow-lg transition-all text-center inline-flex items-center justify-center gap-2 cursor-pointer disabled:opacity-75"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Submitting...</span>
                                    </>
                                ) : (
                                    <span>Submit Request</span>
                                )}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Auth Modal for Unauthenticated Users */}
            <Dialog open={isAuthModalOpen} onOpenChange={setIsAuthModalOpen}>
                <DialogContent className="sm:max-w-md p-6 rounded-2xl border border-slate-100 shadow-xl overflow-hidden">
                    <div className="flex flex-col items-center text-center pt-2">
                        <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-4 ring-8 ring-blue-50/50">
                            <UserPlus className="w-7 h-7" />
                        </div>
                        <DialogHeader className="items-center text-center">
                            <DialogTitle className="text-xl font-bold text-slate-900 tracking-tight">
                                Request Service
                            </DialogTitle>
                            <DialogDescription className="text-sm text-slate-600 mt-2 max-w-xs leading-relaxed">
                                Sign up or log in to Arthaverse to request this service
                            </DialogDescription>
                        </DialogHeader>
                    </div>

                    <DialogFooter className="mt-6 sm:mt-8 flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3 w-full">
                        <button
                            type="button"
                            onClick={() => setIsAuthModalOpen(false)}
                            className="w-full sm:w-1/2 py-2.5 px-4 text-sm font-semibold text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200/80 rounded-xl transition-all text-center cursor-pointer"
                        >
                            Cancel
                        </button>
                        <Link
                            href="/join-arthaverse"
                            className="w-full sm:w-1/2 py-2.5 px-4 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl shadow-md hover:shadow-lg transition-all text-center inline-flex items-center justify-center gap-1.5 cursor-pointer"
                            onClick={() => setIsAuthModalOpen(false)}
                        >
                            <span>Join Arthaverse</span>
                        </Link>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default RequestServiceButton;
